import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Check, List, Minus, UserPlus } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { Role } from '../../auth/roles';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { inputClass } from '../../components/ui/Input';
import { cn } from '../../utils/cn';
import { TempPasswordPanel } from '../../components/TempPasswordPanel';

type B2bRole = 'B2B_OWNER' | 'B2B_BUYER' | 'B2B_VIEWER';
const B2B_ROLE_LIST: B2bRole[] = [Role.B2B_OWNER, Role.B2B_BUYER, Role.B2B_VIEWER];

/** Short description per role, surfaced in the create dropdown (i18n keys, resolved at render). */
const ROLE_BLURB: Record<B2bRole, string> = {
  B2B_OWNER: 'adminScreens.b2bUsers.blurb.B2B_OWNER',
  B2B_BUYER: 'adminScreens.b2bUsers.blurb.B2B_BUYER',
  B2B_VIEWER: 'adminScreens.b2bUsers.blurb.B2B_VIEWER',
};

/**
 * What each B2B role can do, mirroring the backend permission matrix (auth/permissions.ts).
 * Informational only — enforcement stays server-side. Reflects the catalogue reference search
 * (SALES_SEARCH) and ordering features all three roles now reach.
 */
const CAPABILITIES: { label: string; allow: Record<B2bRole, boolean> }[] = [
  {
    label: 'adminScreens.b2bUsers.capabilities.search',
    allow: { B2B_OWNER: true, B2B_BUYER: true, B2B_VIEWER: true },
  },
  {
    label: 'adminScreens.b2bUsers.capabilities.pricing',
    allow: { B2B_OWNER: true, B2B_BUYER: true, B2B_VIEWER: true },
  },
  {
    label: 'adminScreens.b2bUsers.capabilities.placeOrders',
    allow: { B2B_OWNER: true, B2B_BUYER: true, B2B_VIEWER: false },
  },
  {
    label: 'adminScreens.b2bUsers.capabilities.viewOrders',
    allow: { B2B_OWNER: true, B2B_BUYER: true, B2B_VIEWER: true },
  },
  {
    label: 'adminScreens.b2bUsers.capabilities.cancelOrders',
    allow: { B2B_OWNER: true, B2B_BUYER: true, B2B_VIEWER: false },
  },
  {
    label: 'adminScreens.b2bUsers.capabilities.viewUsers',
    allow: { B2B_OWNER: true, B2B_BUYER: false, B2B_VIEWER: false },
  },
];

/** Screens 41-42 — B2B user management. Only ADM/OWN/MGR can create. */
export default function B2bUsers() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const roleLabel = (r: string): string =>
    t(`adminScreens.b2bUsers.roles.${r}`, { defaultValue: r });
  const [customerId, setCustomerId] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<B2bRole>(Role.B2B_BUYER);
  const [issued, setIssued] = useState<{
    email: string;
    password: string;
    emailSent?: boolean;
    emailError?: string;
  } | null>(null);
  const [users, setUsers] = useState<
    Array<{ id: string; email: string; role: string; active: boolean }>
  >([]);

  const list = async () => {
    if (!customerId) return;
    try {
      const { data } = await apiClient.get(`/b2b/users/by-customer/${customerId}`);
      setUsers(data);
    } catch (err) {
      showApiError(err);
    }
  };
  const create = async () => {
    try {
      const { data } = await apiClient.post<{
        email: string;
        tempPassword?: string;
        emailSent?: boolean;
        emailError?: string;
      }>(
        '/b2b/users',
        { customerId, email, role },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      // The password used to be thrown away here; keep it on screen like every other create.
      if (data.tempPassword) {
        setIssued({
          email: data.email,
          password: data.tempPassword,
          emailSent: data.emailSent,
          emailError: data.emailError,
        });
      }
      push({ tone: 'success', message: t('adminScreens.b2bUsers.created') });
      setEmail('');
      await list();
    } catch (err) {
      showApiError(err);
    }
  };

  // Same field surface as every other screen — see components/ui/Input.
  const inputCls = inputClass;

  return (
    <div>
      <PageHeader
        title={t('adminScreens.b2bUsers.title')}
        description={
          <Trans i18nKey="adminScreens.b2bUsers.description" components={{ code: <code /> }} />
        }
      />
      {issued && (
        <TempPasswordPanel
          email={issued.email}
          password={issued.password}
          emailSent={issued.emailSent}
          emailError={issued.emailError}
          onDismiss={() => setIssued(null)}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Look up company */}
        <section className="rounded-lg border bg-card p-4 shadow-sm">
          <h2 className="text-sm font-semibold">{t('adminScreens.b2bUsers.company.title')}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('adminScreens.b2bUsers.company.hint')}
          </p>
          <div className="mt-3 flex gap-2">
            <input
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder={t('adminScreens.b2bUsers.company.placeholder')}
              className={`${inputCls} font-mono`}
            />
            <Button variant="outline" size="sm" onClick={list} disabled={!customerId}>
              <List className="h-4 w-4" />
              {t('adminScreens.b2bUsers.company.list')}
            </Button>
          </div>

          {users.length > 0 && (
            <ul className="mt-4 divide-y rounded-md border">
              {users.map((u) => (
                <li key={u.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="truncate max-w-[18rem]" title={u.email}>
                    {u.email}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <Badge variant="muted">{roleLabel(u.role)}</Badge>
                    {!u.active && (
                      <Badge variant="warning">{t('adminScreens.b2bUsers.inactive')}</Badge>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Create user */}
        <section className="rounded-lg border bg-card p-4 shadow-sm">
          <h2 className="text-sm font-semibold">{t('adminScreens.b2bUsers.create.title')}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('adminScreens.b2bUsers.create.hint')}
          </p>
          <div className="mt-3 space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('adminScreens.b2bUsers.create.email')}
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder={t('adminScreens.b2bUsers.create.emailPlaceholder')}
                className={inputCls}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('adminScreens.b2bUsers.create.role')}
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as B2bRole)}
                className={inputCls}
              >
                {B2B_ROLE_LIST.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)} — {t(ROLE_BLURB[r])}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="success"
              size="sm"
              onClick={create}
              disabled={!customerId || !email}
              className="w-full"
            >
              <UserPlus className="h-4 w-4" />
              {t('adminScreens.b2bUsers.create.submit')}
            </Button>
            {!customerId && (
              <p className="text-xs text-muted-foreground">
                {t('adminScreens.b2bUsers.create.needCustomer')}
              </p>
            )}
          </div>
        </section>
      </div>

      {/* Role capabilities — mirrors the backend permission matrix; the selected role is highlighted. */}
      <section className="mt-4 rounded-lg border bg-card p-4 shadow-sm">
        <h2 className="text-sm font-semibold">{t('adminScreens.b2bUsers.matrix.title')}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('adminScreens.b2bUsers.matrix.hint')}
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-left font-medium">
                  {t('adminScreens.b2bUsers.matrix.capability')}
                </th>
                {B2B_ROLE_LIST.map((r) => (
                  <th
                    key={r}
                    className={cn(
                      'px-3 py-2 text-center font-medium',
                      r === role && 'rounded-t bg-muted/60 text-foreground'
                    )}
                  >
                    {roleLabel(r)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITIES.map((c) => (
                <tr key={c.label} className="border-b last:border-0">
                  <td className="py-2 pr-3">{t(c.label)}</td>
                  {B2B_ROLE_LIST.map((r) => (
                    <td
                      key={r}
                      className={cn('px-3 py-2 text-center', r === role && 'bg-muted/40')}
                    >
                      {c.allow[r] ? (
                        <Check className="mx-auto h-4 w-4 text-success" />
                      ) : (
                        <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
